import { Component, computed, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavItem } from '../../core/models/nav-item.model';
import { AuthService } from '../../core/services/auth.service';
import { NAV_ITEMS, SIDEBAR_ROUTE_ICONS } from '../../core/services/nav.service';
import { TabService } from '../../core/services/tab.service';

export interface PrimaryNavItem {
  id: string;
  label: string;
  icon: string;
  route?: string;
  submenuKey?: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './sidebar.html',
})
export class SidebarComponent {
  private router = inject(Router);
  private tabService = inject(TabService);
  readonly authService = inject(AuthService);

  readonly drawerOpen = input<boolean>(false);
  readonly isMobile = input<boolean>(false);
  readonly collapsed = input<boolean>(false);
  readonly closeDrawer = output<void>();
  readonly toggleCollapse = output<void>();

  readonly navItems = NAV_ITEMS;
  readonly secondaryVisible = signal(true);
  readonly expandedSections = signal<Set<string>>(
    new Set(['inventory', 'purchasing', 'marketplace', 'crm', 'reports', 'ai']),
  );
  readonly currentUrl = signal(this.router.url);

  readonly primaryItems: PrimaryNavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: SIDEBAR_ROUTE_ICONS.dashboard,
      route: '/erp-dashboard',
    },
    {
      id: 'key-modules',
      label: 'Manage API Key',
      icon: SIDEBAR_ROUTE_ICONS.keyModules,
      route: '/api-key-integration',
    },
    {
      id: 'usage',
      label: 'Usage',
      icon: SIDEBAR_ROUTE_ICONS.usage,
      route: '/usage',
    },
    // {
    //   id: 'configure-document',
    //   label: 'Configure Document',
    //   icon: SIDEBAR_ROUTE_ICONS.configureDocument,
    //   route: '/configure-document',
    // },
    {
      id: 'integration',
      label: 'Integration',
      icon: SIDEBAR_ROUTE_ICONS.integrationV2,
      route: '/integration',
    },
    {
      id: 'webhooks',
      label: 'Webhooks',
      icon: SIDEBAR_ROUTE_ICONS.webhooks,
      route: '/webhooks',
    },
    // {
    //   id: 'playground',
    //   label: 'Playground',
    //   icon: SIDEBAR_ROUTE_ICONS.playground,
    //   route: '/playground',
    // },
    // {
    //   id: 'integration-v1',
    //   label: 'Integration v1',
    //   icon: SIDEBAR_ROUTE_ICONS.integration,
    //   route: '/integration-v1',
    // },
  ];

  readonly bottomItems: PrimaryNavItem[] = [
    {
      id: 'profile',
      label: 'My Profile',
      icon: SIDEBAR_ROUTE_ICONS.profile,
      route: '/profile',
    },
  ];

  readonly activePrimary = signal<string>('key-modules');
  readonly isCollapsed = computed(() => !this.isMobile() && this.collapsed());

  /**
   * Items for the secondary panel — only when the active primary has a module submenu.
   * Route-only primaries (Dashboard, API keys, Usage, Profile, …) get no tree here.
   */
  readonly activeSecondaryItems = computed(() => {
    const item = this.activePrimaryItem();
    if (!item?.submenuKey) return [];
    return this.navItems.filter((n) => n.id === item.submenuKey);
  });

  /** Secondary column lists at least one module with children (submenu mode). */
  readonly canShowSecondaryNav = computed(() => this.activeSecondaryItems().length > 0);
  readonly canRenderSecondaryPanel = computed(
    () => this.canShowSecondaryNav() && this.secondaryVisible() && !this.isCollapsed(),
  );

  readonly activePrimaryItem = computed(() => {
    const id = this.activePrimary();
    return (
      this.primaryItems.find((p) => p.id === id) ??
      this.bottomItems.find((p) => p.id === id) ??
      this.primaryItems[1]
    );
  });

  readonly secondaryTitle = computed(() => {
    const item = this.activePrimaryItem();
    return item?.submenuKey ? item.label : 'Navigation';
  });

  constructor() {
    effect(() => {
      const item = this.activePrimaryItem();
      if (!item?.submenuKey && this.secondaryVisible()) {
        this.secondaryVisible.set(false);
      }
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.currentUrl.set((e as NavigationEnd).urlAfterRedirects));

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        const url = this.router.url;
        if (url === '/erp-dashboard') this.activePrimary.set('dashboard');
        else if (url.startsWith('/api-key-integration')) this.activePrimary.set('key-modules');
        else if (url.startsWith('/key-modules')) this.activePrimary.set('key-modules');
        else if (url.startsWith('/usage')) this.activePrimary.set('usage');
        else if (url.startsWith('/configure-document'))
          this.activePrimary.set('configure-document');
        else if (url.startsWith('/webhooks')) this.activePrimary.set('webhooks');
        else if (url.startsWith('/integration-v1')) this.activePrimary.set('integration-v1');
        else if (url.startsWith('/integration')) this.activePrimary.set('integration');
        else if (url.startsWith('/profile')) this.activePrimary.set('profile');
        else this.activePrimary.set('key-modules');
      });
  }

  selectPrimary(item: PrimaryNavItem): void {
    if (item.route) {
      this.activePrimary.set(item.id);
      const allLeafs = this.navItems.flatMap((n) => (n.children?.length ? n.children : [n]));
      const navItem = allLeafs.find((n) => n.route === item.route);
      if (navItem) {
        this.tabService.openTab({ id: navItem.id, title: navItem.label, route: item.route });
      } else {
        this.tabService.openTab({ id: item.id, title: item.label, route: item.route });
      }
      this.router.navigate([item.route]);
      if (this.isMobile()) this.closeDrawer.emit();
    } else {
      if (this.activePrimary() === item.id) {
        this.secondaryVisible.set(!this.secondaryVisible());
      } else {
        this.activePrimary.set(item.id);
        this.secondaryVisible.set(true);
      }
    }
  }

  selectBottom(item: PrimaryNavItem): void {
    this.activePrimary.set(item.id);
    this.tabService.openTab({ id: item.id, title: item.label, route: item.route! });
    this.router.navigate([item.route!]);
    if (this.isMobile()) this.closeDrawer.emit();
  }

  toggleSection(id: string): void {
    this.expandedSections.update((set) => {
      const next = new Set(set);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  isExpanded(id: string): boolean {
    return this.expandedSections().has(id);
  }

  isActive(route: string): boolean {
    const url = this.currentUrl();
    return url === route || url.startsWith(route + '/');
  }

  isSectionActive(item: NavItem): boolean {
    return item.children?.some((c) => !!c.route && this.isActive(c.route)) ?? false;
  }

  navigate(item: NavItem): void {
    if (!item.route) return;
    this.tabService.openTab({ id: item.id, title: item.label, route: item.route });
    this.router.navigate([item.route]);
    if (this.isMobile()) this.closeDrawer.emit();
  }

  logout(): void {
    this.authService.logout();
  }

  userInitials(): string {
    return this.authService.displayInitials();
  }

  userDisplayName(): string {
    return this.authService.displayName();
  }

  userEmail(): string {
    const p = this.authService.apiProfile();
    const u = this.authService.currentUser();
    return p?.email?.trim() || u?.email || '';
  }
}
